import React, { useState } from 'react';
import { captureError } from '@/utils/errorHandler';

export function RemoveFromQueueModal({ 
  isOpen, 
  onClose, 
  appId, 
  appName, 
  authFetch, 
  showToast, 
  onUpdate 
}) {
  const [message, setMessage] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      const response = await authFetch('/api/admin/app-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          action: 'remove_from_queue',
          message: message.trim() || null,
        }),
      });
      
      if (response.ok) {
        showToast('App removed from queue', 'success');
        handleClose();
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove from queue');
      }
    } catch (error) {
      captureError(error, {
        action: 'handleRemoveFromQueue',
        component: 'RemoveFromQueueModal',
        extra: { appId, message },
      });
      showToast(error.message || 'Failed to remove from queue', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">❌ Remove from Queue</h3>
        
        <p className="text-gray-600 mb-4">
          This will remove <strong>{appName}</strong> from the build queue. The user will no longer see this app as "in progress".
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message to user (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Explain why this app/request isn't suitable..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            This message will be sent to the user in the conversation.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={isRemoving}
          >
            Cancel
          </button>
          <button
            onClick={handleRemove}
            disabled={isRemoving}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isRemoving ? 'Removing...' : 'Remove from Queue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MoveToProductionModal({ 
  isOpen, 
  onClose, 
  appId, 
  devUrl, 
  authFetch, 
  showToast, 
  onUpdate 
}) {
  const [productionUrl, setProductionUrl] = useState(devUrl || '');
  const [isMoving, setIsMoving] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setProductionUrl('');
    onClose();
  };

  const handleMove = async () => {
    if (!productionUrl.trim()) {
      showToast('Please enter a production URL', 'error');
      return;
    }
    
    setIsMoving(true);
    try {
      const response = await authFetch('/api/apps/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: appId,
          appUrl: productionUrl.trim(),
          approvalStatus: null,
          status: 'live',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to move to production');
      }

      showToast('✅ Moved to production', 'success');
      handleClose();
      if (onUpdate) onUpdate();
    } catch (error) {
      captureError(error, {
        action: 'handleMoveToProduction',
        component: 'MoveToProductionModal',
        extra: { appId, productionUrl },
      });
      console.error('Error moving to production:', error);
      showToast(error.message || 'Failed to move to production', 'error');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">🚀 Move to Production</h3>
        
        <p className="text-gray-600 mb-4">
          Have you completed all manual production setup (database updates, config changes, etc.)?
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Production URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={productionUrl}
            onChange={(e) => setProductionUrl(e.target.value)}
            placeholder="https://production.example.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          {devUrl && (
            <p className="mt-1 text-xs text-gray-500">
              Current dev URL: <span className="font-mono">{devUrl}</span>
            </p>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={isMoving}
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={isMoving || !productionUrl.trim()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isMoving ? 'Moving...' : '🚀 Move to Production'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function FinishWorkModal({ 
  isOpen, 
  onClose, 
  appId, 
  appName,
  appData,
  authFetch, 
  showToast, 
  onUpdate
}) {
  const [note, setNote] = useState('');
  const [devUrl, setDevUrl] = useState('');
  const [useExistingDevUrl, setUseExistingDevUrl] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setNote('');
    setDevUrl('');
    setUseExistingDevUrl(false);
    onClose();
  };

  const handleFinish = async () => {
    const finalDevUrl = useExistingDevUrl ? appData?.devUrl : devUrl.trim();
    if (!finalDevUrl) {
      showToast('Please provide a dev URL or use the existing one', 'error');
      return;
    }

    setIsFinishing(true);
    try {
      const response = await authFetch('/api/admin/app-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          action: 'finish',
          devUrl: finalDevUrl,
          note: note.trim() || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to finish work');
      }

      const data = await response.json();
      showToast(`✅ ${data.message}`, 'success');
      handleClose();
      if (onUpdate) await onUpdate();
    } catch (error) {
      captureError(error, {
        action: 'handleFinishWork',
        component: 'FinishWorkModal',
        extra: { appId, note, devUrl },
      });
      console.error('Error finishing work:', error);
      showToast(error.message || 'Failed to finish work', 'error');
    } finally {
      setIsFinishing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-4">✅ Finish Work & Request Approval</h3>
        
        <p className="text-gray-600 mb-4">
          You're about to finish work on <strong>{appName}</strong>. This will send an email to the customer with the dev URL.
        </p>

        {/* Final Payment Notice for Quote-based Apps */}
        {appData?.selectedPaymentMethod === 'quote' && appData?.quotedAt && appData?.depositPaidAt && !appData?.finalPaidAt && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💰 Final Payment:</strong> A final payment request email will be automatically sent to the customer after you finish work.
            </p>
          </div>
        )}
        
        {/* Dev URL Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Development URL <span className="text-red-500">*</span>
          </label>
          {appData?.devUrl && (
            <div className="mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useExistingDevUrl}
                  onChange={(e) => {
                    setUseExistingDevUrl(e.target.checked);
                    if (e.target.checked) {
                      setDevUrl(appData.devUrl);
                    } else {
                      setDevUrl('');
                    }
                  }}
                  className="w-4 h-4 text-zapt-purple border-gray-300 rounded focus:ring-zapt-purple"
                />
                <span className="text-sm text-gray-700">
                  Use existing dev URL: <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{appData.devUrl}</span>
                </span>
              </label>
            </div>
          )}
          {!useExistingDevUrl && (
            <input
              type="url"
              value={devUrl}
              onChange={(e) => setDevUrl(e.target.value)}
              placeholder="https://dev.example.com"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          )}
          <p className="mt-1 text-xs text-gray-500">
            This URL will be included in the email sent to the customer
          </p>
        </div>
        
        {/* Note Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Note - describe what you did <span className="text-red-500">*</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe the work completed..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            This will be included in the email and saved to the app's conversation
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={isFinishing}
          >
            Cancel
          </button>
          <button
            onClick={handleFinish}
            disabled={isFinishing || (!useExistingDevUrl && !devUrl.trim()) || !note.trim()}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isFinishing ? 'Finishing...' : 'Finish & Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
